import { useEffect, useMemo, useRef } from 'react';

import { useSearchParams } from 'react-router-dom';

import { useMutation, useLazyQuery } from '@apollo/client';
import { fastProvider } from '@tradener/lumen';
import { debounce } from 'lodash';
import { nanoid } from 'nanoid';

import CREATE_SIMULATION, {
  ResponseProps as CreateResponseProps,
  PayloadProps as CreatePayloadProps,
} from '@graphql/mutation/createSimulation';
import NEW_PORTFOLIO from '@graphql/query/newPortfolio';
import SIMULATION, { ResponseProps, PayloadProps } from '@graphql/query/simulation';

import usePortfolio, { getVariables } from '~/pages/Portfolio/usePortfolio';
import cache from '~/services/link/cache';
import DEFAULT_ACCOUNTS from '~/utils/getDefaultAccounts';
import { getCachedTenants } from '~/utils/getTenants';

import useSimulation from '../../useSimulation';
import useSimulationRules from '../../useSimulationRules';
import compose from './compose';

const [withSimulationTable, useSimulationTable] = fastProvider(() => {
  const portfolio = usePortfolio();
  const { simulationId, setSimulation, setFilter } = useSimulation();
  const [searchParams] = useSearchParams();
  const simulationParam = searchParams.get('simulation');
  const showPreview = searchParams.has('preview');
  const { isPortfolio } = useSimulationRules();
  const query = isPortfolio ? NEW_PORTFOLIO : SIMULATION;
  const { hasGiro, periodType, closeDateLte, closeDateGte, accountingPortfolio, internalAccounts, tenantId, ...rest } =
    getVariables(portfolio, { showPreview });

  const [createSimulation] = useMutation<CreateResponseProps, CreatePayloadProps>(CREATE_SIMULATION, {
    variables: { input: { name: null } },
    onCompleted: ({ createSimulation }) => setFilter('simulationId', createSimulation.simulation.id),
  });

  const { expandedPeriods, ...restWithoutExpandedPeriods } = rest;
  const expandedPeriodsRef = useRef(expandedPeriods);

  const stableExpandedPeriods = isPortfolio ? expandedPeriodsRef.current : expandedPeriods;

  const variablesKey = JSON.stringify({
    ...restWithoutExpandedPeriods,
    ...(isPortfolio ? (simulationParam ? { simulationName: simulationParam } : {}) : { simulationId }),
    excludedContracts: Object.keys(portfolio.excludedContracts),
  });

  const queryVariables = useMemo(
    () => ({
      ...JSON.parse(variablesKey),
      hasGiro,
      periodType,
      closeDateLte,
      closeDateGte,
      accountingPortfolio,
      internalAccounts,
      ...(isPortfolio ? {} : { expandedPeriods: stableExpandedPeriods }),
    }),
    [
      variablesKey,
      hasGiro,
      periodType,
      closeDateLte,
      closeDateGte,
      accountingPortfolio,
      internalAccounts,
      isPortfolio,
      stableExpandedPeriods,
    ],
  );

  const [executeQuery, { data, loading, ...response }] = useLazyQuery<ResponseProps, Partial<PayloadProps>>(query, {
    notifyOnNetworkStatusChange: true,
    fetchPolicy: 'no-cache',
    canonizeResults: false,
    onCompleted: (data) => {
      if (data?.simulation) {
        setSimulation(data?.simulation?.simulation, isPortfolio);
      }
    },
    onError: ({ message }) => {
      if (message === 'not_found') createSimulation();
    },
  });

  const debouncedExecuteQuery = useMemo(() => debounce(executeQuery, 800), [executeQuery]);

  useEffect(() => {
    if (!internalAccounts?.length) return;

    const variables = {
      ...queryVariables,
    };

    debouncedExecuteQuery({ variables });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [queryVariables, internalAccounts?.length, debouncedExecuteQuery]);

  useEffect(
    () => () => {
      cache.evict({ id: 'ROOT_QUERY', fieldName: 'simulation' });
    },
    [],
  );

  // TODO - remove this when filter is migrated from tenantId to internalAccounts
  useEffect(() => {
    if (!internalAccounts?.length) {
      const prevTenants = getCachedTenants()
        ?.filter((tenant) => tenantId?.includes(tenant.id))
        ?.map(({ name }) => name);
      const relatedAccounts = DEFAULT_ACCOUNTS.filter(({ name }) => prevTenants?.includes(name))?.map(
        ({ accountId }) => accountId,
      );

      portfolio.setFilter('internalAccounts', relatedAccounts);
    }
  }, [internalAccounts?.length, portfolio, tenantId]);

  return {
    ...response,
    data,
    loading,
    suffix: nanoid(),
    simulationPeriods: loading ? compose() : data?.simulation?.simulationPeriods,
    simulationOperations: loading ? [] : data?.simulation?.simulationOperations,
  };
});

export { withSimulationTable, useSimulationTable };
